import React, { useEffect, useMemo, useState } from "react";
import { useMutation, useQuery } from "@apollo/client";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faChevronDown, faChevronRight, faSearch } from "@fortawesome/free-solid-svg-icons";
import { Alert, Badge, Button, Card, Form, InputGroup, Modal, Spinner } from "react-bootstrap";
import { toast } from "react-toastify";
import { BULK_TOGGLE_TENANT_DISPLAY_FEATURE } from "../../graphql/mutations";
import { GET_TENANT_DISPLAY_FEATURES } from "../../graphql/queries";
import {
  createCatalystFeatureHierarchy,
  createFeatureState,
  createFeatureToggleInput,
  featureDisplayPathKey,
  featurePathKey,
  getCatalystFeatureSupport,
  getChangedFeatureCount,
  getVisibleFeatureItems,
  toggleFeatureState
} from "../../utils/displayFeatures";
import "./FeatureConfigPage.css";

const MAIN_ROUTER_REQUIREMENT_MESSAGE = "At least one Main Routers feature must remain enabled.";

const BulkFeatureAssignmentModal = ({ onApplied, onHide, show, tenantIds = [], tenants = [] }) => {
  const sourceTenantId = tenantIds[0] || null;
  const [draft, setDraft] = useState({});
  const [expanded, setExpanded] = useState({});
  const [searchTerm, setSearchTerm] = useState("");
  const [step, setStep] = useState("select"); // "select" | "confirm" | "result"
  const [discarding, setDiscarding] = useState(false);
  const [result, setResult] = useState(null);

  const { data, loading, error } = useQuery(GET_TENANT_DISPLAY_FEATURES, {
    variables: { tenantId: sourceTenantId },
    skip: !show || !sourceTenantId,
    fetchPolicy: "network-only"
  });
  const sourceItems = useMemo(() => data?.tenantDisplayFeatures?.items || [], [data]);
  // Bulk assign applies to an arbitrary set of tenants, so there's no single meaningful
  // "current" state to start from - only the tenant-independent default is. `tenantId` above
  // is only needed to satisfy the query, since the catalog shape and default state are the
  // same regardless of which tenant is queried; its own `enabled` is never used.
  const templateItems = useMemo(() => sourceItems.map((item) => ({ ...item, enabled: item.defaultEnabled })), [sourceItems]);
  const items = useMemo(() => createCatalystFeatureHierarchy(templateItems), [templateItems]);
  const visibleItems = getVisibleFeatureItems(items, expanded, searchTerm);
  const [bulkToggle, { loading: saving }] = useMutation(BULK_TOGGLE_TENANT_DISPLAY_FEATURE);

  useEffect(() => {
    if (show) {
      setSearchTerm("");
      setStep("select");
      setDiscarding(false);
      setResult(null);
    }
  }, [show]);

  useEffect(() => {
    if (!items.length) {
      return;
    }
    setDraft(createFeatureState(items));
    setExpanded(
      items.reduce((state, item) => {
        if (item.hasChildren && item.depth <= 1) {
          state[featureDisplayPathKey(item)] = true;
        }
        return state;
      }, {})
    );
  }, [items]);

  const changedCount = getChangedFeatureCount(items, draft);
  const isDirty = changedCount > 0;
  const changedFeatures = items.filter((item) => Boolean(draft[featurePathKey(item.path)]) !== Boolean(item.enabled));
  const selectedTenantNames = tenantIds.map((tenantId) => tenants.find((tenant) => tenant.id === tenantId)?.name || tenantId);
  const ready = isDirty && tenantIds.length > 0;

  const requestClose = () => {
    if (saving) {
      return;
    }
    if (isDirty) {
      setDiscarding(true);
      return;
    }
    onHide();
  };

  const submit = async () => {
    if (!ready) {
      return;
    }
    try {
      const features = createFeatureToggleInput(items, draft);
      const response = await bulkToggle({
        variables: { tenantIds, features }
      });
      const updateResult = response.data?.bulkToggleTenantDisplayFeature;
      const failedTenantIds = updateResult?.failedTenantIds || [];
      setResult({
        updatedCount: updateResult?.updatedCount || 0,
        failedTenantIds
      });
      setStep("result");
      onApplied?.(failedTenantIds);
    } catch (mutationError) {
      toast.error(
        mutationError?.message?.includes("At least one Main Routers feature")
          ? MAIN_ROUTER_REQUIREMENT_MESSAGE
          : "Bulk feature update failed. Please try again."
      );
      setStep("select");
    }
  };

  return (
    <Modal show={show} onHide={requestClose} size="xl" centered backdrop={saving ? "static" : true}>
      <Modal.Header closeButton={!saving}>
        <Modal.Title>Bulk Assign Features</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {discarding ? (
          <>
            <Alert variant="warning" className="mb-3">
              You have {changedCount} unsaved {changedCount === 1 ? "change" : "changes"}. Discard them and close?
            </Alert>
          </>
        ) : result ? (
          <>
            <Alert variant={result.failedTenantIds.length ? "danger" : "success"}>
              Updated {result.updatedCount} tenant{result.updatedCount === 1 ? "" : "s"}.
            </Alert>
            {result.failedTenantIds.length > 0 && (
              <div>
                <strong>Failed tenant IDs</strong>
                <ul className="mb-0 mt-2">
                  {result.failedTenantIds.map((tenantId) => (
                    <li key={tenantId}>{tenantId}</li>
                  ))}
                </ul>
              </div>
            )}
          </>
        ) : error ? (
          <Alert variant="danger">Could not load display features. Please try again.</Alert>
        ) : loading ? (
          <div className="py-4 text-center">
            <Spinner animation="border" role="status" />
          </div>
        ) : step === "confirm" ? (
          <>
            <Alert variant="info" className="mb-3">
              Confirm the following <strong>{changedFeatures.length}</strong> feature change
              {changedFeatures.length === 1 ? "" : "s"} for <strong>{tenantIds.length}</strong> tenant
              {tenantIds.length === 1 ? "" : "s"}.
            </Alert>
            <div className="feature-bulk-tenant-panel mb-3">
              <div className="small fw-semibold mb-2">Feature changes ({changedFeatures.length})</div>
              <div className="feature-tenant-badge-list">
                {changedFeatures.map((feature) => {
                  const nextEnabled = Boolean(draft[featurePathKey(feature.path)]);
                  return (
                    <Badge
                      key={featurePathKey(feature.path)}
                      bg={nextEnabled ? "success" : "light"}
                      text={nextEnabled ? undefined : "dark"}
                      pill
                      className="feature-tenant-badge"
                    >
                      {nextEnabled ? "Enable" : "Disable"}: {feature.path.join(" / ")}
                    </Badge>
                  );
                })}
              </div>
            </div>
            <div className="feature-bulk-tenant-panel">
              <div className="small fw-semibold mb-2">Affected tenants ({selectedTenantNames.length})</div>
              <div className="feature-tenant-badge-list">
                {selectedTenantNames.map((tenantName, index) => (
                  <Badge key={`${tenantName}-${index}`} bg="light" text="dark" pill className="feature-tenant-badge">
                    {tenantName}
                  </Badge>
                ))}
              </div>
            </div>
          </>
        ) : (
          <Form>
            <Form.Group className="mb-3">
              <Form.Label htmlFor="bulk-feature-search" className="fw-semibold">
                Features
              </Form.Label>
              <InputGroup className="mb-2">
                <InputGroup.Text>
                  <FontAwesomeIcon icon={faSearch} />
                </InputGroup.Text>
                <Form.Control
                  id="bulk-feature-search"
                  type="search"
                  value={searchTerm}
                  onChange={(event) => setSearchTerm(event.target.value)}
                  placeholder="Search by feature name or path"
                />
              </InputGroup>
              <div className="feature-tree feature-tree-scrollable" aria-label="Toggle features to apply">
                <div className="feature-tree-header">
                  <span>Feature</span>
                  <span>Default state</span>
                  <span className="text-end">Enabled</span>
                </div>
                {visibleItems.map((item) => {
                  const key = featurePathKey(item.path);
                  const displayKey = featureDisplayPathKey(item);
                  const isExpanded = Boolean(expanded[displayKey]);
                  const catalystSupport = getCatalystFeatureSupport(item, sourceItems);

                  return (
                    <div className={`feature-tree-row catalyst-support-${catalystSupport.status}`} key={key}>
                      <div style={{ paddingLeft: `${Math.max(item.depth, 0) * 24}px` }}>
                        <div className="d-flex align-items-center">
                          {item.hasChildren ? (
                            <Button
                              variant="link"
                              className="feature-name-button p-0 me-2"
                              onClick={() => setExpanded((current) => ({ ...current, [displayKey]: !current[displayKey] }))}
                              aria-label={`${isExpanded ? "Collapse" : "Expand"} ${item.name}`}
                              aria-expanded={isExpanded}
                            >
                              <FontAwesomeIcon icon={isExpanded ? faChevronDown : faChevronRight} fixedWidth />
                            </Button>
                          ) : (
                            <span style={{ width: "22px" }} aria-hidden="true" />
                          )}
                          <div>
                            <div className="fw-semibold">{item.name}</div>
                            <div className="feature-path">{item.path.join(" / ")}</div>
                            {catalystSupport.message && (
                              <div className={`feature-support-note feature-support-note-${catalystSupport.status}`}>
                                {catalystSupport.message}
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                      <div className="feature-default-column">
                        <Badge bg={item.defaultEnabled ? "success" : "light"} text={item.defaultEnabled ? undefined : "dark"}>
                          {item.defaultEnabled ? "Enabled" : "Disabled"}
                        </Badge>
                      </div>
                      <div className="text-end">
                        <Form.Check
                          type="switch"
                          className="d-inline-block"
                          id={`bulk-feature-toggle-${key}`}
                          aria-label={`Toggle ${item.name}`}
                          checked={Boolean(draft[key])}
                          onChange={() => setDraft((current) => toggleFeatureState(items, current, item))}
                        />
                      </div>
                    </div>
                  );
                })}
                {!visibleItems.length && <div className="bg-white text-muted text-center py-5">No matching features found.</div>}
              </div>
            </Form.Group>
          </Form>
        )}
      </Modal.Body>
      {!discarding && step === "select" && !result && !loading && !error && isDirty && (
        <Card className="feature-save-bar mx-3 mb-3">
          <Card.Body className="d-flex flex-wrap align-items-center justify-content-between gap-3 py-2">
            <strong>
              {changedCount} unsaved {changedCount === 1 ? "change" : "changes"}
            </strong>
          </Card.Body>
        </Card>
      )}
      <Modal.Footer>
        {discarding ? (
          <>
            <Button variant="secondary" type="button" onClick={() => setDiscarding(false)}>
              Keep editing
            </Button>
            <Button variant="danger" type="button" onClick={onHide}>
              Discard changes
            </Button>
          </>
        ) : result ? (
          <Button variant="secondary" type="button" onClick={onHide}>
            Close
          </Button>
        ) : (
          <>
            <Button
              variant="secondary"
              type="button"
              onClick={step === "confirm" ? () => setStep("select") : requestClose}
              disabled={saving}
            >
              {step === "confirm" ? "Back" : "Cancel"}
            </Button>
            {!loading &&
              !error &&
              (step === "confirm" ? (
                <Button variant="primary" type="button" onClick={submit} disabled={saving}>
                  {saving && <Spinner animation="border" size="sm" className="me-2" />}
                  Apply changes
                </Button>
              ) : (
                <Button variant="primary" type="button" onClick={() => setStep("confirm")} disabled={!ready}>
                  Review changes
                </Button>
              ))}
          </>
        )}
      </Modal.Footer>
    </Modal>
  );
};

export default BulkFeatureAssignmentModal;
